import React, { useState, useRef, useEffect } from 'react';
import type { IconType } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../lib/i18n';

interface ServiceCardProps {
  id: string;
  title: string;
  subheadline: string;
  teaser: string;
  description: string;
  icon: IconType;
  rotationDegrees: number;
  onCardClick: (id: string) => void;
  isVisible: boolean;
}

export function ServiceCard({ 
  id,
  title, 
  subheadline, 
  teaser, 
  description, 
  icon: Icon,
  rotationDegrees,
  onCardClick,
  isVisible
}: ServiceCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const { language } = useLanguage();
  const [translations, setTranslations] = useState<any>({});

  useEffect(() => {
    const loadTranslations = async () => {
      const homeTranslations = await getTranslation(language, 'home');
      setTranslations(homeTranslations);
    };
    loadTranslations();
  }, [language]);

  const handleClick = () => {
    onCardClick(id);
  };

  if (!translations.buttons) {
    return null;
  }

  return (
    <div 
      className={`flip-card aspect-[1] min-w-[380px] transition-opacity duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      onClick={handleClick}
    >
      <div 
        ref={cardRef}
        className="flip-card-inner"
        style={{ transform: `rotateY(${rotationDegrees}deg)` }}
      >
        <div className="flip-card-front bg-white rounded-lg shadow-sm p-6 md:p-8 flex flex-col h-full">
          <div className="text-bamboo-600 mb-4">
            <Icon className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-semibold mb-2">{title}</h3>
          <h4 className="text-bamboo-600 text-sm mb-4">{subheadline}</h4>
          <p className="text-gray-600 flex-grow line-clamp-3">{teaser}</p>
          <button className="mt-4 text-bamboo-600 hover:text-bamboo-700 flex items-center gap-1 text-sm group">
            {translations.buttons.details}
            <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
        <div className="flip-card-back bg-white rounded-lg shadow-sm p-6 md:p-8 h-full overflow-auto">
          <div className="flow-root">
            <div className="float-left mr-4">
              <Icon className="w-8 h-8 text-bamboo-600" />
            </div>
            <p className="text-gray-600">{description}</p>
          </div>
        </div>
      </div>
    </div>
  );
}